import { useReducer, useMemo } from 'react';
import { PLAN_NAMES_NISCHIT_PENSION } from '../../constants';
import { logDebug } from '../../common/utils/logger';

/** Set to true to trace ROP/ECBO toggle state in console (dev only). */
const DEBUG_QUOTE_TOGGLES = false;
const debugLog = (...args) => {
  if (DEBUG_QUOTE_TOGGLES && import.meta.env?.DEV) {
    logDebug('[Quote Toggles]', ...args);
  }
};

// ── Primary Rider IDs (mutually exclusive group) ──
export const PRIMARY_RIDER_IDS = [
  'accidental-death',
  'accidental-death-disability',
  'accidental-death-disability-plus',
];

// ── Initial state ──
const INITIAL_STATE = {
  /** Default Plus until lead assigns Elite — avoids wrong product code / modal flash for Plus-only journeys. */
  selectedPlan: null,
  selectedCoverAmount: null,
  customCoverAmount: '',
  coverAmountError: '',
  selectedCoverTillAge: null,
  customCoverTillAge: '',
  coverTillAgeError: '',
  selectedPolicyTerm: null,
  showHelpModal: false,
  showPlanIneligibleModal: false,
  showPremiumRefundModal: false,
  showEnhancedCoverageModal: false,
  premiumRefundEnabled: false,
  enhancedCoverageEnabled: false,
  /** True when plan was auto-switched to Plus because user turned ROP ON while on Elite; used to switch back to Elite when ROP is turned OFF */
  switchedToPlusDueToROP: false,
  paymentTermType: 'Regular Pay',
  premiumFrequency: 'Yearly',
  selectedLimitedPayTerm: 5,
  showMoreRiders: false,
  selectedRiders: new Set(),
  riderCovers: {},
  submitting: false,
  submitError: null,
  showSuccessModal: false,
};

// ── Reducer ──
function quoteReducer(state, action) {
  switch (action.type) {
    case 'SET_LANDING_DEFAULTS':
      // Always set ROP and ECBO to false and return new state so UI re-renders (default landing = NO).
      debugLog('SET_LANDING_DEFAULTS: forcing premiumRefundEnabled=false, enhancedCoverageEnabled=false');
      return {
        ...state,
        premiumRefundEnabled: false,
        enhancedCoverageEnabled: false,
      };

    case 'SET':
      if (action.field === 'premiumRefundEnabled' || action.field === 'enhancedCoverageEnabled') {
        debugLog('SET', action.field, '→', action.value, '(current:', state[action.field], ')');
      }
      // Bail out if value hasn't changed — prevents unnecessary re-renders
      if (state[action.field] === action.value) return state;
      return { ...state, [action.field]: action.value };

    case 'TOGGLE_RIDER': {
      const newSelected = new Set(state.selectedRiders);
      const isPrimary = PRIMARY_RIDER_IDS.includes(action.riderId);
      if (newSelected.has(action.riderId)) {
        newSelected.delete(action.riderId);
      } else if (isPrimary) {
        PRIMARY_RIDER_IDS.forEach((id) => { if (id !== action.riderId) newSelected.delete(id); });
        newSelected.add(action.riderId);
      } else {
        newSelected.add(action.riderId);
      }
      return { ...state, selectedRiders: newSelected };
    }

    case 'UPDATE_RIDER_COVER':
      return {
        ...state,
        riderCovers: { ...state.riderCovers, [action.riderId]: parseInt(action.value, 10) },
      };

    case 'SET_RIDERS_FROM_REDUX': {
      const incomingIds = Array.isArray(action.selectedRiderIds)
        ? action.selectedRiderIds
        : [];
      const incomingCovers =
        action.riderCovers && typeof action.riderCovers === 'object'
          ? action.riderCovers
          : {};

      // Keep at most one from the primary rider mutually-exclusive group.
      const primaryHits = incomingIds.filter((id) => PRIMARY_RIDER_IDS.includes(id));
      const nonPrimary = incomingIds.filter((id) => !PRIMARY_RIDER_IDS.includes(id));

      const nextSelected = new Set([...nonPrimary]);
      if (primaryHits.length > 0) nextSelected.add(primaryHits[0]);

      const normalizedCovers = Object.fromEntries(
        Object.entries(incomingCovers).map(([k, v]) => [
          k,
          v != null && v !== '' ? parseInt(String(v), 10) : state.riderCovers[k],
        ]),
      );

      return {
        ...state,
        selectedRiders: nextSelected,
        riderCovers: { ...state.riderCovers, ...normalizedCovers },
      };
    }

    default:
      return state;
  }
}

/**
 * useQuoteState — encapsulates Quote page state in a single useReducer.
 *
 * Returns every state field as a top-level key, plus individual
 * setter functions that match the old useState API so the rest
 * of the component code needs minimal changes.
 *
 * @param {Object} initialRiderCovers - Map of riderId → minCover
 */
const useQuoteState = (initialRiderCovers) => {
  const [state, dispatch] = useReducer(quoteReducer, {
    ...INITIAL_STATE,
    riderCovers: initialRiderCovers,
  });

  debugLog('useQuoteState init/render', {
    premiumRefundEnabled: state.premiumRefundEnabled,
    enhancedCoverageEnabled: state.enhancedCoverageEnabled,
  });

  // Stable setter functions — dispatch is guaranteed stable by React,
  // so the memoised object never recalculates.
  const actions = useMemo(() => ({
    setSelectedPlan: (v) => dispatch({ type: 'SET', field: 'selectedPlan', value: v }),
    setSelectedCoverAmount: (v) => dispatch({ type: 'SET', field: 'selectedCoverAmount', value: v }),
    setCustomCoverAmount: (v) => dispatch({ type: 'SET', field: 'customCoverAmount', value: v }),
    setCoverAmountError: (v) => dispatch({ type: 'SET', field: 'coverAmountError', value: v }),
    setSelectedCoverTillAge: (v) => dispatch({ type: 'SET', field: 'selectedCoverTillAge', value: v }),
    setCustomCoverTillAge: (v) => dispatch({ type: 'SET', field: 'customCoverTillAge', value: v }),
    setCoverTillAgeError: (v) => dispatch({ type: 'SET', field: 'coverTillAgeError', value: v }),
    setSelectedPolicyTerm: (v) => dispatch({ type: 'SET', field: 'selectedPolicyTerm', value: v }),
    setShowHelpModal: (v) => dispatch({ type: 'SET', field: 'showHelpModal', value: v }),
    setShowPlanIneligibleModal: (v) => dispatch({ type: 'SET', field: 'showPlanIneligibleModal', value: v }),
    setShowPremiumRefundModal: (v) => dispatch({ type: 'SET', field: 'showPremiumRefundModal', value: v }),
    setShowEnhancedCoverageModal: (v) => dispatch({ type: 'SET', field: 'showEnhancedCoverageModal', value: v }),
    setPremiumRefundEnabled: (v) => dispatch({ type: 'SET', field: 'premiumRefundEnabled', value: v }),
    setEnhancedCoverageEnabled: (v) => dispatch({ type: 'SET', field: 'enhancedCoverageEnabled', value: v }),
    applyLandingDefaults: () => dispatch({ type: 'SET_LANDING_DEFAULTS' }),
    setSwitchedToPlusDueToROP: (v) => dispatch({ type: 'SET', field: 'switchedToPlusDueToROP', value: v }),
    setPaymentTermType: (v) => dispatch({ type: 'SET', field: 'paymentTermType', value: v }),
    setPremiumFrequency: (v) => dispatch({ type: 'SET', field: 'premiumFrequency', value: v }),
    setSelectedLimitedPayTerm: (v) => dispatch({ type: 'SET', field: 'selectedLimitedPayTerm', value: v }),
    setShowMoreRiders: (v) => dispatch({ type: 'SET', field: 'showMoreRiders', value: v }),
    setSubmitting: (v) => dispatch({ type: 'SET', field: 'submitting', value: v }),
    setSubmitError: (v) => dispatch({ type: 'SET', field: 'submitError', value: v }),
    setShowSuccessModal: (v) => dispatch({ type: 'SET', field: 'showSuccessModal', value: v }),
    // Complex / compound actions
    toggleRider: (riderId) => dispatch({ type: 'TOGGLE_RIDER', riderId }),
    handleRiderCoverChange: (riderId, value) => dispatch({ type: 'UPDATE_RIDER_COVER', riderId, value }),
    setRidersFromRedux: (selectedRiderIds, riderCovers) =>
      dispatch({ type: 'SET_RIDERS_FROM_REDUX', selectedRiderIds, riderCovers }),
  }), []);

  return { ...state, ...actions, dispatch };
};

export default useQuoteState;
